import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import { ArrowLeft, CheckCircle, Building } from 'lucide-react';
import ContactForm from '../../components/forms/ContactForm';
import { Transformer } from '../../types/transformer';
import { transformers as transformersData } from '../../utils/transformers';
import SEO from '../../components/seo/SEO';

const TransformerDetailPage: React.FC = () => {
  const { slug } = useParams();
  const [transformer, setTransformer] = useState<Transformer | null>(null);
  const [relatedTransformers, setRelatedTransformers] = useState<Transformer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = () => {
      setLoading(true);
      if (!slug) {
        setError('Transformer slug is required');
        setLoading(false);
        return;
      }

      const decodedSlug = decodeURIComponent(slug);
      const transformers = transformersData;
      const data = transformers.find((t) => t.slug === decodedSlug) || null;

      if (data) {
        setTransformer(data);
        const related = transformers
          .filter((t) => t.id !== data.id && t.type === data.type)
          .slice(0, 3);
        setRelatedTransformers(related);
        setError(null);
      } else {
        setError('Трансформатор не найден');
        setTransformer(null);
      }
      setLoading(false);
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="ml-4 text-gray-600">Загрузка данных...</p>
      </div>
    );
  }

  if (error || !transformer) {
    return (
      <div className="container-custom py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Ошибка</h1>
        <p className="text-gray-600 mb-6">{error || 'Трансформатор не найден'}</p>
        <Link to="/catalog/transformers" className="btn-primary inline-flex items-center">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Вернуться к списку трансформаторов
        </Link>
      </div>
    );
  }

  const advantages = [
    'Пожаробезопасность и экологичность',
    'Снижение расходов на эксплуатацию на 5-10%',
    'Минимальные затраты на монтаж и обслуживание',
    'Компактные размеры по сравнению с масляными трансформаторами',
    'Длительный срок службы – более 30 лет',
    'Повышенная стойкость к перегрузкам и коротким замыканиям',
  ];

  const applications = [
    'Промышленные предприятия',
    'Торговые и офисные центры',
    'Жилые комплексы',
    'Объекты инфраструктуры',
    'Медицинские учреждения',
    'Дата-центры',
    'Сельскохозяйственные объекты',
    'Образовательные учреждения',
    'Спортивные комплексы',
    'Транспортные узлы',
  ] as const;

  type ApplicationType = (typeof applications)[number];

  const applicationDetails: Record<ApplicationType, string> = {
    'Промышленные предприятия': 'Надежное электроснабжение производственных линий и оборудования',
    'Торговые и офисные центры':
      'Безопасное питание систем освещения, вентиляции и кондиционирования',
    'Жилые комплексы': 'Стабильное электроснабжение жилых помещений и общественных зон',
    'Объекты инфраструктуры': 'Бесперебойное питание систем жизнеобеспечения и безопасности',
    'Медицинские учреждения': 'Надежное электроснабжение медицинского оборудования и систем',
    'Дата-центры': 'Высококачественное питание серверного оборудования и систем охлаждения',
    'Сельскохозяйственные объекты':
      'Эффективное электроснабжение сельскохозяйственной техники и систем',
    'Образовательные учреждения': 'Безопасное питание учебного оборудования и систем безопасности',
    'Спортивные комплексы': 'Надежное электроснабжение спортивного оборудования и освещения',
    'Транспортные узлы': 'Бесперебойное питание систем управления и безопасности',
  };

  return (
    <>
      <SEO
        title={transformer.name ?? 'Трансформатор'}
        description={transformer.description ?? ''}
        product={{
          name: transformer.name ?? 'Трансформатор',
          description: transformer.description ?? '',
          image: transformer.image ?? '',
          brand: 'Проминнова',
          category: 'Трансформаторы',
        }}
      />

      <div className="container-custom py-12">
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Image and Specs */}
          <div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
              <img
                src={
                  transformer.image
                    ? `/images/transformers/${transformer.image}.jpg`
                    : '/images/transformers/default.jpg'
                }
                alt={transformer.name ?? 'Трансформатор'}
                className="w-full h-auto"
                loading="lazy"
                width="600"
                height="600"
              />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">Технические характеристики</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Тип</span>
                  <span className="font-medium">{transformer.type || 'Не указано'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Мощность</span>
                  <span className="font-medium">
                    {transformer.power ? `${transformer.power} кВА` : 'Не указано'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Напряжение</span>
                  <span className="font-medium">
                    {transformer.voltage ? `${transformer.voltage} кВ` : 'Не указано'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Материал обмотки</span>
                  <span className="font-medium">{transformer.material || 'Не указано'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Защита</span>
                  <span className="font-medium">{transformer.protection || 'Не указано'}</span>
                </div>
                {transformer.weight && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Вес</span>
                    <span className="font-medium">{transformer.weight} кг</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Области применения</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {applications.map((application) => (
                  <div key={application} className="flex items-start">
                    <Building className="w-5 h-5 text-primary-500 mr-2 mt-1" />
                    <div>
                      <h4 className="font-medium">{application}</h4>
                      <p className="text-sm text-gray-600">{applicationDetails[application]}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Description and Contact */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h1 className="text-2xl font-bold mb-4">{transformer.name}</h1>
              <p className="text-gray-600 mb-6">{transformer.description}</p>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Преимущества:</h3>
                <ul className="space-y-2">
                  {advantages.map((advantage, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />
                      <span>{advantage}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-2 border-primary-500">
              <h3 className="text-xl font-semibold mb-2">Оставьте заявку</h3>
              <p className="text-gray-600 mb-6">
                Заполните форму, и наш менеджер свяжется с вами в течение 2 часов (в рабочее время).
              </p>
              <ContactForm />
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedTransformers.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-8">Похожие трансформаторы</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedTransformers.map((related) => (
                <div key={related.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <Link to={`/catalog/transformers/${related.slug}`}>
                    <img
                      src={
                        related.image
                          ? `/images/transformers/${related.image}.jpg`
                          : '/images/transformers/default.jpg'
                      }
                      alt={related.name ?? 'Трансформатор'}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                      width="341"
                      height="192"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold mb-2">{related.name}</h3>
                      <div className="text-sm text-gray-600">
                        <p>Мощность: {related.power} кВА</p>
                        <p>Напряжение: {related.voltage} кВ</p>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TransformerDetailPage;
